import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Container } from '../ui/Container/Container';
import { useScrollReveal } from './useScrollReveal';

const IS_ALPHA = import.meta.env.VITE_IS_ALPHA === 'true';

const FAQ_ITEMS = [
  {
    q: 'What AI models are included?',
    a: 'All of them. Your subscription includes access to 30+ image models (Nano Banana, FLUX, Seedream, Recraft, Ideogram, GPT Image, and more), 20+ video models (Sora, Veo, Kling, Seedance, Hailuo, LTX, WAN), and free text generation. New models are added regularly at no extra cost.',
  },
  {
    q: 'How do credits work?',
    a: 'Each generation costs credits depending on the model. Budget models cost as little as 1 credit per image. Mid-tier models like Seedream 4.5, FLUX Kontext, and Recraft V3 cost around 13 credits. Premium models cost more. You get 4,000 credits per month, and text generation is always free.',
  },
  {
    q: 'Can multiple people work on the same canvas?',
    a: 'Yes. Cascady supports real-time collaborative editing. Multiple team members can work on the same canvas simultaneously with live cursor tracking and presence indicators. You can also share any canvas via a link for others to view or duplicate.',
  },
  {
    q: 'Can I use my own reference images?',
    a: 'Absolutely. Upload reference images, mood boards, brand assets, or old work directly onto the canvas. Connect them to generate nodes as references. Some models support up to 14 reference images for a single generation.',
  },
  {
    q: 'Is there a free trial?',
    a: IS_ALPHA
      ? 'Yes! Sign up and explore the platform freely — browse canvases, templates, and all AI models. When you\'re ready to generate, start a 7-day free trial with full access to every model. No charge until the trial ends.'
      : 'Text generation is always free. To generate images and video, choose a plan starting at $19/month with 4,000 credits — enough for hundreds of images and dozens of video clips.',
  },
];

export function LandingFaq() {
  const ref = useScrollReveal<HTMLDivElement>();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-12 md:py-20 bg-[#f5f7fa]" id="faq">
      <Container size="lg" className="max-w-[1100px]">
        <div ref={ref} className="lp-reveal text-center">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-primary-500 mb-3">
            FAQ
          </p>
          <h2
            className="text-[clamp(1.75rem,4vw,2.6rem)] font-semibold leading-[1.15] tracking-tight"
            style={{ color: '#1d2939' }}
          >
            Questions? Answered.
          </h2>
        </div>

        <div className="max-w-[620px] mx-auto mt-8">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={i} className="border-b border-black/[0.06]">
                <button
                  className="w-full flex items-center justify-between py-[1.1rem] text-left bg-transparent border-none cursor-pointer"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                >
                  <span className="text-[0.9rem] font-medium text-[#1d2939]">{item.q}</span>
                  <ChevronDown
                    className="w-4 h-4 flex-shrink-0 ml-4 transition-transform duration-200 text-[#8c95a6]"
                    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </button>
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: isOpen ? 300 : 0, opacity: isOpen ? 1 : 0 }}
                >
                  <p className="pb-[1.1rem] text-[0.82rem] text-[#5a6478] leading-relaxed">
                    {item.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
